import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { fxUniforms } from './common';

const hash = (n: number) => { const x = Math.sin(n * 127.1 + 311.7) * 43758.5453; return x - Math.floor(x); };

/**
 * The ice wall skill's look in one draw: faceted ice chunks and pointed crystal
 * pillars erupt in a wave; inner light, a cold rim and racing cracks follow its
 * strength; blows make it shiver; it shatters outward or melts into the road. All of
 * that runs in the vertex/fragment shader from a handful of uniforms.
 */
export class BarrierView {
  readonly root = new THREE.Group();
  private readonly uniforms = {
    uAge: { value: 0 }, uHealth: { value: 1 }, uHit: { value: 0 }, uBreak: { value: 0 }, uSink: { value: 0 },
  };
  private readonly bar: THREE.Mesh;
  private readonly barMat: THREE.ShaderMaterial;
  private state: 'hidden' | 'up' | 'break' | 'sink' = 'hidden';
  private outro = 0;
  private lag = 1;

  constructor(scene: THREE.Scene) {
    const parts: THREE.BufferGeometry[] = [];
    // Every piece is a faceted ice chunk: indexed vertices are jittered first (so faces stay closed),
    // then de-indexed for flat, crystalline normals.
    const chunk = (g: THREE.BufferGeometry, seed: number, amount: number) => {
      const p = g.attributes.position;
      for (let i = 0; i < p.count; i++) {
        const x = p.getX(i), y = p.getY(i), z = p.getZ(i);
        const h1 = hash(seed * 31 + Math.round(x * 97) * 7 + Math.round(y * 89) * 13 + Math.round(z * 83) * 17);
        const h2 = hash(seed * 17 + Math.round(x * 71) * 3 + Math.round(y * 67) * 11 + Math.round(z * 61) * 5);
        p.setXYZ(i, x * (1 + (h1 - 0.5) * amount), y * (1 + (h2 - 0.5) * amount * 0.6), z * (1 + (h2 - h1) * amount));
      }
      const flat = g.toNonIndexed(); g.dispose(); flat.computeVertexNormals();
      return flat;
    };
    const block = (geos: THREE.BufferGeometry[], x: number, y: number, z: number, ry: number, delay: number, seed: number) => {
      const g = mergeGeometries(geos)!; geos.forEach((q) => q.dispose());
      g.applyMatrix4(new THREE.Matrix4().makeRotationY(ry).setPosition(x, y, z));
      const n = g.attributes.position.count, center = new Float32Array(n * 3), info = new Float32Array(n * 4);
      // chunks fly away from the wall's middle and slightly toward the horde when it breaks
      const ang = Math.atan2(z, -0.6) + (seed - 0.5) * 0.8;
      for (let i = 0; i < n; i++) {
        center.set([x, y, z], i * 3);
        info.set([delay, seed, Math.cos(ang) * (0.6 + seed * 0.6), Math.sin(ang) * (0.6 + seed * 0.6)], i * 4);
      }
      g.setAttribute('aCenter', new THREE.BufferAttribute(center, 3));
      g.setAttribute('aBlock', new THREE.BufferAttribute(info, 4));
      parts.push(g);
    };
    let k = 0;
    for (let row = 0; row < 3; row++) for (let i = 0; i < 6; i++) {
      const z = (i - 2.5) * 0.38 + (row % 2 ? 0.05 : -0.05), s = hash(++k);
      const w = 0.3 + s * 0.08, h = 0.26 + hash(k + 40) * 0.08, d = 0.36 - hash(k + 80) * 0.03;
      block([chunk(new THREE.BoxGeometry(w, h, d, 2, 2, 2), k, 0.28)], (s - 0.5) * 0.05, 0.135 + row * 0.28 + (h - 0.3) * 0.5, z,
        (hash(k + 120) - 0.5) * 0.2, Math.abs(z) * 0.12 + row * 0.06, s);
    }
    // pillars become tall hexagonal crystals with points, leaning a little apart
    for (const z of [-1.04, 0, 1.04]) {
      const s = hash(++k), tall = 1.05 + s * 0.25;
      const shaft = chunk(new THREE.CylinderGeometry(0.15, 0.2, tall, 6, 2).translate(0, tall / 2, 0), k, 0.12);
      const tip = chunk(new THREE.ConeGeometry(0.15, 0.32, 6).translate(0, tall + 0.16, 0), k + 3, 0.1);
      const side = chunk(new THREE.CylinderGeometry(0.07, 0.1, tall * 0.55, 6, 1).translate(0.06, tall * 0.27, 0.14).rotateX(0.25), k + 5, 0.12);
      block([shaft, tip, side], 0, 0, z, s * 2, Math.abs(z) * 0.08, s);
      void tall;
    }
    const geo = mergeGeometries(parts)!; parts.forEach((g) => g.dispose());

    const mat = new THREE.MeshStandardMaterial({ color: 0xa6d6ec, roughness: 0.12, metalness: 0, envMapIntensity: 1.8 });
    mat.onBeforeCompile = (sh) => {
      Object.assign(sh.uniforms, this.uniforms, { uTime: fxUniforms.uTime });
      sh.vertexShader = sh.vertexShader
        .replace('#include <common>', `#include <common>
          attribute vec3 aCenter; attribute vec4 aBlock;
          uniform float uTime, uAge, uHit, uBreak, uSink;
          varying vec3 vLocal, vLocalN; varying float vSeed, vBreak;`)
        .replace('#include <begin_vertex>', `#include <begin_vertex>
          vLocal = position; vLocalN = normal; vSeed = aBlock.y;
          float rise = clamp((uAge - aBlock.x) / 0.24, 0.0, 1.0), r1 = rise - 1.0;
          float lift = 1.0 + 2.7 * r1 * r1 * r1 + 1.7 * r1 * r1;          // ease-out-back: erupts and settles
          transformed.y -= (1.0 - lift) * (aCenter.y + 0.45);
          float sink = clamp((uSink - aBlock.x * 0.6) / 0.5, 0.0, 1.0);
          transformed.y -= sink * sink * (aCenter.y + 0.6);
          float b = clamp(uBreak * 1.25 - aBlock.y * 0.25, 0.0, 1.0);
          vBreak = b;
          if (b > 0.0) {
            vec3 off = transformed - aCenter;
            float c = cos(b * 3.0 * (aBlock.y - 0.5)), s = sin(b * 3.0 * (aBlock.y - 0.5));
            off.xy = mat2(c, s, -s, c) * off.xy;
            transformed = aCenter + off * (1.0 - 0.6 * b) + vec3(aBlock.z, 0.0, aBlock.w) * b * 1.2 + vec3(0.0, b * 1.6 - b * b * 2.4, 0.0);
          }
          transformed.x += sin(uTime * 90.0 + aBlock.y * 20.0) * uHit * 0.014;`);
      sh.fragmentShader = sh.fragmentShader
        .replace('#include <common>', `#include <common>
          uniform float uTime, uHealth, uHit;
          varying vec3 vLocal, vLocalN; varying float vSeed, vBreak;
          float bh(vec3 p) { return fract(sin(dot(p, vec3(12.9898, 78.233, 37.719))) * 43758.5453); }
          float bn(vec3 p) {
            vec3 i = floor(p), f = fract(p); f = f * f * (3.0 - 2.0 * f);
            return mix(mix(mix(bh(i), bh(i + vec3(1,0,0)), f.x), mix(bh(i + vec3(0,1,0)), bh(i + vec3(1,1,0)), f.x), f.y),
                       mix(mix(bh(i + vec3(0,0,1)), bh(i + vec3(1,0,1)), f.x), mix(bh(i + vec3(0,1,1)), bh(i + vec3(1,1,1)), f.x), f.y), f.z);
          }`)
        .replace('#include <color_fragment>', `#include <color_fragment>
          // clear ice: deep blue where it is thick, pale where it thins, frost settled on upward faces
          float depthN = bn(vLocal * 4.5 + vSeed * 9.0) * 0.7 + bn(vLocal * 13.0) * 0.3;
          vec3 deep = vec3(0.12, 0.36, 0.58), pale = vec3(0.72, 0.9, 1.0);
          diffuseColor.rgb = mix(deep, pale, smoothstep(0.25, 0.85, depthN));
          float frost = smoothstep(0.45, 0.85, vLocalN.y) * (0.55 + 0.45 * bn(vLocal * 22.0));
          diffuseColor.rgb = mix(diffuseColor.rgb, vec3(0.92, 0.97, 1.0), frost * 0.8);`)
        .replace('#include <emissivemap_fragment>', `#include <emissivemap_fragment>
          {
            float weak = 1.0 - uHealth;
            float flicker = uHealth < 0.35 ? 0.6 + 0.4 * step(0.4, fract(uTime * 6.0 + vSeed * 3.0)) : 1.0;
            // light caught inside the ice and a cold rim where the surface turns away from the eye
            float inner = bn(vLocal * 6.0 + vec3(0.0, uTime * 0.15, 0.0) + vSeed * 5.0);
            float fres = pow(1.0 - abs(dot(normalize(normal), normalize(vViewPosition))), 3.0);
            vec3 cyan = vec3(0.25, 0.75, 1.1);
            totalEmissiveRadiance += cyan * (0.08 + 0.22 * uHealth) * smoothstep(0.35, 0.9, inner) * flicker;
            totalEmissiveRadiance += vec3(0.5, 0.85, 1.2) * fres * (0.35 + uHit * 1.2);
            // cracks race through the ice as its strength drains
            float ridge = abs(bn(vLocal * 6.5 + vSeed * 7.0) - 0.5);
            float crack = smoothstep(0.045 * weak, 0.0, ridge) * smoothstep(0.05, 0.35, weak);
            totalEmissiveRadiance += vec3(0.7, 0.95, 1.2) * crack * (0.9 + uHit * 2.0);
            totalEmissiveRadiance += cyan * vBreak * (1.0 - vBreak) * 1.6;
            diffuseColor.rgb = mix(diffuseColor.rgb, vec3(1.0), crack * 0.5);
          }`);
    };
    mat.customProgramCacheKey = () => 'barrier-ice-wall-v1';
    const wall = new THREE.Mesh(geo, mat);
    wall.castShadow = false; wall.receiveShadow = true; wall.frustumCulled = false;
    this.root.add(wall);

    this.barMat = new THREE.ShaderMaterial({
      transparent: true, depthWrite: false, depthTest: false,
      uniforms: { uFill: { value: 1 }, uLag: { value: 1 }, uHit: { value: 0 }, uShow: { value: 0 }, uTime: fxUniforms.uTime },
      vertexShader: 'varying vec2 vUv; void main(){ vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }',
      fragmentShader: /* glsl */ `
        uniform float uFill, uLag, uHit, uShow, uTime; varying vec2 vUv;
        void main() {
          vec2 p = vUv;
          float frame = step(p.x, 0.012) + step(0.988, p.x) + step(p.y, 0.16) + step(0.84, p.y);
          float x = (p.x - 0.02) / 0.96;
          float fill = step(x, uFill), lag = step(x, uLag) * (1.0 - fill);
          float seg = smoothstep(0.012, 0.0, abs(fract(x * 10.0) - 0.5) - 0.48);
          float low = uFill < 0.3 ? 0.65 + 0.35 * sin(uTime * 12.0) : 1.0;
          vec3 c = vec3(0.03, 0.05, 0.06);
          c = mix(c, mix(vec3(0.12, 0.5, 0.65), vec3(0.45, 1.2, 1.5), p.y) * low * (1.0 + uHit), fill);
          c = mix(c, vec3(1.4, 1.3, 1.1), lag);
          c *= 1.0 - seg * 0.55;
          c = mix(c, vec3(0.55, 0.45, 0.32), clamp(frame, 0.0, 1.0));
          gl_FragColor = vec4(c, 0.92 * uShow);
        }`,
    });
    this.bar = new THREE.Mesh(new THREE.PlaneGeometry(1.2, 0.085), this.barMat);
    this.bar.position.y = 1.4; this.bar.renderOrder = 12;
    this.root.add(this.bar);
    this.root.name = 'skillBarrier'; this.root.visible = false;
    scene.add(this.root);
  }

  get visible() { return this.state !== 'hidden'; }

  raise(x: number, z: number) {
    this.root.position.set(x, 0, z); this.root.visible = true; this.state = 'up';
    this.uniforms.uAge.value = 0; this.uniforms.uBreak.value = 0; this.uniforms.uSink.value = 0;
    this.uniforms.uHealth.value = 1; this.uniforms.uHit.value = 0; this.lag = 1;
  }

  /** Shatter outward (destroyed) or sink back into the road (expired). */
  end(shattered: boolean) { if (this.state === 'up') { this.state = shattered ? 'break' : 'sink'; this.outro = 0; } }

  update(dt: number, health: number, hit: number, camera: THREE.Camera) {
    if (this.state === 'hidden') return;
    const u = this.uniforms;
    u.uAge.value += dt;
    if (this.state === 'up') { u.uHealth.value = health; u.uHit.value = hit; }
    else {
      this.outro += dt; u.uHit.value = Math.max(0, u.uHit.value - dt * 5);
      if (this.state === 'break') u.uBreak.value = Math.min(1, this.outro / 0.55);
      else u.uSink.value = this.outro;
      if (this.outro > (this.state === 'break' ? 0.6 : 1.1)) { this.state = 'hidden'; this.root.visible = false; }
    }
    // the lost strength lingers as a pale chunk, then drains to the new value
    this.lag = this.lag < health ? health : this.lag + (health - this.lag) * Math.min(1, dt * 3.5);
    const m = this.barMat.uniforms;
    m.uFill.value = health; m.uLag.value = this.lag; m.uHit.value = hit;
    m.uShow.value = this.state === 'up' ? Math.min(1, u.uAge.value / 0.3) : Math.max(0, 1 - this.outro * 4);
    this.bar.quaternion.copy(camera.quaternion);
  }
}
